










































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import HasValidation from "@/mixins/HasValidation";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import { DataModules } from "@/store/modules/data";
import { IClient } from "@/models/clients";
import { ICurrency } from "@/models/constants";
import { mapState } from "vuex";
import { IState } from "@/store";
import { WALLET_REFUND_SOURCE } from "@/data/enums/wallet";
import { IWalletBalance } from "@/models/wallets";
import { IAccount } from "@/models/accounts";

@Component({
  components: {
    "manual-refund": () =>
      import("@/components/app/global/wallets/manualRefund.vue")
  },
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/balanceScope`](this.accountId);
      },
      wallet(state, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class RefundFromWalletModal extends Mixins(
  CurrenciesStore,
  HasValidation
) {
  // Props
  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: "" })
  public currencyId!: ICurrency["id"];

  // Data
  public WALLET_REFUND_SOURCE = WALLET_REFUND_SOURCE;
  public wallet!: IWalletBalance;
  public currency: ICurrency = {} as ICurrency;
  public dataScope!: string;
  public isProcessing = false;
  public dataModule = DataModules.WALLETS;
  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public canSeeWallet = this.$userCan("wallet_admin_get_balance");
  public refundSource: WALLET_REFUND_SOURCE | null = null;
  public manuaRefundForm = null;
  public form = {
    amount: 0
  };

  // Lifecycle methods
  public async created() {
    await this.getBalance();
    if (!this.onlineAmount && !!this.offlineAmount) {
      this.refundSource = WALLET_REFUND_SOURCE.MANUAL;
    } else {
      this.refundSource = WALLET_REFUND_SOURCE.ORIGINAL_SOURCE;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: this.dataModule,
      scope: this.dataScope,
      vm: this
    });
  }

  // Getters
  public get maxValue() {
    if (this.refundSource === WALLET_REFUND_SOURCE.ORIGINAL_SOURCE)
      return this.wallet.online[this.currency?.code]?.amount_converted || 0;
    if (this.refundSource === WALLET_REFUND_SOURCE.MANUAL)
      return this.wallet.offline[this.currency?.code]?.amount_converted || 0;
    return 0;
  }

  public get currencyCodes() {
    return this.$_.isEmpty(this.wallet) ? [] : Object.keys(this.wallet.total);
  }

  public get sanitizedForm() {
    return {
      ...this.form,
      ...(this.refundSource === WALLET_REFUND_SOURCE.MANUAL &&
      this.manuaRefundForm
        ? this.manuaRefundForm
        : {}),
      currency_id: this.currency?.id,
      client_id: this.client?.id,
      accountId: this.accountId
    };
  }

  public get onlineAmount() {
    return this.$_.get(
      this.wallet,
      `online.${this.currency?.code}.amount_converted`,
      0
    );
  }

  public get onlineAmountFormatted() {
    return this.$_.get(
      this.wallet,
      `online.${this.currency?.code}.amount_converted_formatted`
    );
  }

  public get offlineAmount() {
    return this.$_.get(
      this.wallet,
      `offline.${this.currency?.code}.amount_converted`,
      0
    );
  }

  public get offlineAmountFormatted() {
    return this.$_.get(
      this.wallet,
      `offline.${this.currency?.code}.amount_converted_formatted`
    );
  }

  public get accountId(): IAccount["id"] {
    return this.$_.get(this.client, "accounts[0].id", "");
  }

  public get clientBrandId() {
    return this.client?.brand_id;
  }

  // Methods
  public reloadData() {
    this.isReloading = true;
    this.getBalance();
  }

  public async getBalance() {
    this.isLoading = true;
    this.requestError = false;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/balance`, {
        scope: this.dataScope,
        accountId: this.accountId,
        ignoreStored: true,
        vm: this
      });
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async refund() {
    try {
      this.isProcessing = true;
      if (!(await this.validate())) throw this.$t("_.form_invalid");
      await this.$store.dispatch(`data/${this.dataModule}/refund`, {
        scope: this.dataScope,
        accountId: this.accountId,
        ignoreStored: true,
        refundSource: this.refundSource,
        data: this.sanitizedForm,
        vm: this
      });
      this.$emit("success");
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  @Watch("refundSource", { immediate: true })
  public onRefundSourceChange(source) {
    if (source === WALLET_REFUND_SOURCE.ORIGINAL_SOURCE) {
      this.form.amount = this.onlineAmount;
    } else if (source === WALLET_REFUND_SOURCE.MANUAL) {
      this.form.amount = this.offlineAmount;
    }
  }

  @Watch("currenciesLoaded")
  public currenciesLoadedWatcher() {
    const firstCurrency = this.$_.find(
      this.currencies,
      i => i.code === this.$_.first(this.currencyCodes)
    );

    this.currency = this.$_.find(this.currencies, [
      "id",
      this.currencyId || firstCurrency?.id
    ]) as ICurrency;
  }
}
